import React from 'react';
import { Link } from 'react-router-dom';
import { feeRows, rebateExchanges, referralCode } from './rebateData';
import { useLang } from '../i18n/context';
import './RebatePage.css';

function RebatePage() {
  const { t } = useLang();

  const rebindWays = [
    {
      title: t('rebate.rebindBinanceTitle'),
      subtitle: t('rebate.rebindBinanceSubtitle'),
      items: [t('rebate.rebindB1'), t('rebate.rebindB2'), t('rebate.rebindB3')],
    },
    {
      title: t('rebate.rebindOkxTitle'),
      subtitle: t('rebate.rebindOkxSubtitle'),
      items: [t('rebate.rebindO1'), t('rebate.rebindO2'), t('rebate.rebindO3')],
    },
    {
      title: t('rebate.rebindBitgetTitle'),
      subtitle: t('rebate.rebindBitgetSubtitle'),
      items: [t('rebate.rebindBg1'), t('rebate.rebindBg2'), t('rebate.rebindBg3')],
    },
  ];

  const renderFeeCell = (row, cell, index) => {
    const isBestSpotMaker = row[0] === t('rebate.spotMaker') && index === 2;
    if (!isBestSpotMaker) return cell;
    return (
      <span className="rebate-best-rate">
        <span>{cell}</span>
        <span
          className="rebate-best-rate-icon"
          role="img"
          aria-label={`${t('rebate.spotMaker')} ${t('rebate.lowestFee')}`}
          title={`${t('rebate.spotMaker')} ${t('rebate.lowestFee')}`}
        >★</span>
      </span>
    );
  };

  return (
    <main className="rebate-page">
      <section className="rebate-hero">
        <div className="rebate-hero-copy">
          <p className="rebate-kicker">{t('rebate.kicker', { code: referralCode })}</p>
          <h1>{t('rebate.heroTitle')}</h1>
          <p className="rebate-hero-desc">
            {t('rebate.heroDesc', { code: referralCode })}
          </p>
          <div className="rebate-hero-actions">
            <a href="#exchanges" className="rebate-btn-primary">{t('rebate.pickExchange')}</a>
            <a href="#rebind" className="rebate-btn-secondary">{t('rebate.viewRebind')}</a>
          </div>
        </div>
        <div className="rebate-code-panel" aria-label={t('rebate.inviteCode')}>
          <span>{t('rebate.universalCode')}</span>
          <strong>{referralCode}</strong>
          <small>{t('rebate.codeHint')}</small>
        </div>
      </section>

      <section className="rebate-stats" aria-label={t('rebate.overview')}>
        <div><strong>3</strong><span>{t('rebate.statExchanges')}</span></div>
        <div><strong>20%</strong><span>{t('rebate.statRebate')}</span></div>
        <div><strong>{referralCode}</strong><span>{t('rebate.statCode')}</span></div>
      </section>

      <section id="exchanges" className="rebate-section">
        <div className="rebate-section-head">
          <p>{t('rebate.exchSubtitle')}</p>
          <h2>{t('rebate.exchTitle')}</h2>
          <span>{t('rebate.exchDesc')}</span>
        </div>
        <div className="rebate-exchange-grid">
          {rebateExchanges.map((exchange) => (
            <article className={`rebate-exchange-card ${exchange.id}`} key={exchange.id}>
              <Link className="rebate-card-main-link" to={`/rebate/${exchange.id}`} aria-label={t('rebate.viewTutorial', { name: exchange.shortName })}>
                <div className="rebate-exchange-top">
                  <div className="rebate-exchange-title">
                    <img src={exchange.logo} alt={`${exchange.name} logo`} />
                    <div>
                      <p>{exchange.badge}</p>
                      <h3>{exchange.name}</h3>
                    </div>
                  </div>
                  <strong>{exchange.rebate}</strong>
                </div>
                <p className="rebate-exchange-summary">{exchange.summary}</p>
                <div className="rebate-tags">
                  {exchange.tags.map((tag) => <span key={tag}>{tag}</span>)}
                </div>
              </Link>
              <div className="rebate-card-actions">
                <Link to={`/rebate/${exchange.id}`}>{t('rebate.viewTutorial', { name: exchange.shortName })}</Link>
                {exchange.registerUrl !== '#' && (
                  <a href={exchange.registerUrl} target="_blank" rel="noopener noreferrer">{exchange.directCta}</a>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="fees" className="rebate-section">
        <div className="rebate-section-head">
          <p>{t('rebate.feeSubtitle')}</p>
          <h2>{t('rebate.feeTitle')}</h2>
          <span>{t('rebate.feeDesc')}</span>
        </div>
        <div className="rebate-table-wrap">
          <table className="rebate-fee-table">
            <thead>
              <tr>
                <th>{t('rebate.compareItem')}</th>
                <th>{t('rebate.binance')}</th>
                <th>{t('rebate.okx')}</th>
                <th>{t('rebate.bitget')}</th>
              </tr>
            </thead>
            <tbody>
              {feeRows.map((row) => (
                <tr key={row[0]}>
                  {row.map((cell, index) => {
                    const isBestSpotMaker = row[0] === t('rebate.spotMaker') && index === 2;
                    return (
                      <td
                        key={`${row[0]}-${index}`}
                        className={isBestSpotMaker ? 'rebate-fee-best' : undefined}
                      >
                        {renderFeeCell(row, cell, index)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section id="rebind" className="rebate-section">
        <div className="rebate-section-head">
          <p>{t('rebate.rebindSubtitle')}</p>
          <h2>{t('rebate.rebindTitle')}</h2>
          <span>{t('rebate.rebindDesc')}</span>
        </div>
        <div className="rebate-rebind-grid">
          {rebindWays.map((way) => (
            <article className="rebate-rebind-card" key={way.title}>
              <h3>{way.title}</h3>
              <p>{way.subtitle}</p>
              <ul>
                {way.items.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="rebate-cta">
        <h2>{t('rebate.ctaTitle')}</h2>
        <p>{t('rebate.ctaDesc', { code: referralCode })}</p>
        <Link to="/rebate/binance" className="rebate-btn-primary">{t('rebate.ctaBtn')}</Link>
      </section>
    </main>
  );
}

export default RebatePage;
